import React, { useState, useCallback, memo } from 'react'
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Typography,
  Dialog,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  SelectChangeEvent,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import TodayIcon from '@mui/icons-material/Today'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { Patient } from '@/features/patients/types/patients'
import PatientDetailDrawer from '@/apps/legacy/components/portal/PatientDetailDrawer'
import PatientNeedCheckCell from '@/apps/legacy/components/portal/PatientNeedCheckCell'
import FlexibleActionFilters from '@/apps/legacy/components/portal/FlexibleActionFilters'
import { usePatientsQuery } from '@/features/patients'
import { usePatientListUrlParams } from '@/features/internal/patients-portal/hooks/usePatientListUrlParams'
import { useScrollRestoration } from '@/features/internal/patients-portal/hooks/useScrollRestoration'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { PAGINATION_LIMITS } from '@/apps/legacy/constants/patientConstants'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'
import { ActionButton } from '@/apps/legacy/components/portal/CustomButton'

// メモ化されたテーブル行コンポーネント
interface PatientTableRowProps {
  patient: Patient
  onShowDetail: (patient: Patient) => void
}

const PatientTableRow = memo<PatientTableRowProps>(function PatientTableRow({
  patient,
  onShowDetail,
}) {
  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: colorTokens.grey[100],
        },
      }}
    >
      <TableCell>{patient.name_kanji}</TableCell>
      <TableCell>{patient.name_kana}</TableCell>
      <TableCell>{patient.patient_code}</TableCell>
      <TableCell>{patient.last_visit_date}</TableCell>
      <TableCell>
        <PatientNeedCheckCell patient={patient} />
      </TableCell>
      <TableCell align="right">
        <ActionButton
          variant="contained"
          size="small"
          onClick={() => onShowDetail(patient)}
        >
          詳細
        </ActionButton>
      </TableCell>
    </TableRow>
  )
})

const PatientsPage: React.FC = () => {
  const {
    apiParams,
    inputValues,
    visitDate,
    actionStatus,
    actionGroup,
    sortField,
    sortOrder,
    page,
    limit,
    setNameKanji,
    setNameKana,
    setPatientCode,
    setVisitDate,
    setPage,
    setLimit,
    setSort,
    setActionStatus,
    setActionGroup,
    jumpToToday,
    clearAllFilters,
  } = usePatientListUrlParams()

  const { data, isLoading, error } = usePatientsQuery({
    params: apiParams,
  })
  const patients = data?.results ?? []
  const total = data?.total ?? 0

  const { saveScrollPosition, scrollToTop } = useScrollRestoration(
    !isLoading && patients.length > 0
  )

  // 一時的なUI状態のみ useState
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)

  const handleShowDetail = useCallback(
    (patient: Patient) => {
      saveScrollPosition()
      setSelectedPatient(patient)
      setDetailDrawerOpen(true)
    },
    [saveScrollPosition]
  )

  const handleCloseDetail = useCallback(() => {
    setDetailDrawerOpen(false)
    setTimeout(() => setSelectedPatient(null), 300)
  }, [])

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, newPage: number) => {
      setPage(newPage)
    },
    [setPage]
  )

  const handleLimitChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setLimit(event.target.value as number)
    },
    [setLimit]
  )

  const hasActiveFilters =
    inputValues.nameKanji ||
    inputValues.nameKana ||
    inputValues.patientCode ||
    visitDate

  // 初回ローディング時のみ全画面ローディングを表示
  if (isLoading && patients.length === 0) {
    return (
      <PortalLayout>
        <LoadingIndicator />
      </PortalLayout>
    )
  }
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">エラー: {error?.message}</Typography>
      </Box>
    )
  }
  return (
    <PortalLayout>
      <PortalPageHeader title="患者管理" />
      {/* 個別検索条件 */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: colorTokens.text.secondary }}
          >
            患者検索
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<TodayIcon />}
              onClick={() => {
                jumpToToday()
                scrollToTop()
              }}
              sx={{
                py: 0.25,
                px: 1,
                color: colorTokens.blue[600],
              }}
            >
              今日へ移動
            </Button>
            {hasActiveFilters && (
              <Button
                size="small"
                onClick={clearAllFilters}
                sx={{
                  py: 0.25,
                  px: 1,
                  color: colorTokens.grey[600],
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                クリア
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="患者名（漢字）"
            value={inputValues.nameKanji}
            onChange={(e) => setNameKanji(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            size="small"
            placeholder="患者名（カナ）"
            value={inputValues.nameKana}
            onChange={(e) => setNameKana(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            size="small"
            placeholder="患者ID"
            value={inputValues.patientCode}
            onChange={(e) => setPatientCode(e.target.value)}
            sx={{ flex: 1, minWidth: 150 }}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="来局日"
              value={visitDate ? dayjs(visitDate) : null}
              onChange={(v) => setVisitDate(v ? v.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { flex: 1, minWidth: 180 },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
      </Card>

      {/* アクショングループ・ステータスフィルター */}
      <Card sx={{ mb: 3 }}>
        <FlexibleActionFilters
          selectedGroup={actionGroup}
          selectedStatus={actionStatus}
          onGroupChange={(group) => setActionGroup(group)}
          onStatusChange={(status) => setActionStatus(status)}
        />
      </Card>

      {/* 件数表示とページサイズ選択 */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            総件数: {total}件 (第{(page - 1) * limit + 1}件目から第
            {Math.min(page * limit, total)}件目を表示)
          </Typography>
          {isLoading && (
            <Box sx={{ display: 'inline-flex', ml: 1 }}>
              <LoadingIndicator message="" size={16} />
            </Box>
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>表示件数</InputLabel>
          <Select value={limit} onChange={handleLimitChange} label="表示件数">
            {PAGINATION_LIMITS.map((limitOption) => (
              <MenuItem key={limitOption} value={limitOption}>
                {limitOption}件
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* 患者一覧テーブル */}
      <TableContainer
        component={Paper}
        sx={[
          {
            transition: 'opacity 0.2s ease',
          },
          isLoading
            ? {
                opacity: 0.7,
              }
            : {
                opacity: 1,
              },
        ]}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colorTokens.grey[100] }}>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: colorTokens.border.light },
                }}
                onClick={() => setSort('name_kanji')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  患者名（漢字）
                  {sortField === 'name_kanji' &&
                    (sortOrder === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    ))}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: colorTokens.border.light },
                }}
                onClick={() => setSort('name_kana')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  患者名（カナ）
                  {sortField === 'name_kana' &&
                    (sortOrder === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    ))}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: colorTokens.border.light },
                }}
                onClick={() => setSort('patient_code')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  患者ID
                  {sortField === 'patient_code' &&
                    (sortOrder === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    ))}
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: colorTokens.border.light },
                }}
                onClick={() => setSort('last_visit_date')}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  前回来局日
                  {sortField === 'last_visit_date' &&
                    (sortOrder === 'asc' ? (
                      <ArrowUpwardIcon fontSize="small" />
                    ) : (
                      <ArrowDownwardIcon fontSize="small" />
                    ))}
                </Box>
              </TableCell>
              <TableCell>確認事項</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {patients.map((patient) => (
              <PatientTableRow
                key={patient.id}
                patient={patient}
                onShowDetail={handleShowDetail}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページネーション */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={Math.ceil(total / limit)}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
          siblingCount={2}
          boundaryCount={1}
        />
      </Box>

      {/* 患者詳細ドロワー */}
      <Dialog
        open={detailDrawerOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <IconButton onClick={handleCloseDetail}>
            <CloseIcon />
          </IconButton>
        </Box>
        {selectedPatient && (
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <PatientDetailDrawer
              patient={selectedPatient}
              onClose={handleCloseDetail}
            />
          </Box>
        )}
      </Dialog>
    </PortalLayout>
  )
}

export default PatientsPage
